import { inject, Injectable, Injector, resource, signal } from '@angular/core';
import { Router } from '@angular/router';
import { handleRequestError } from '@softline/application';
import { UserInfo, UserInfoService } from '@softline/auth';
import { WorkflowRepository } from '../repositories/workflow.repository';
import { Workflow, WorkflowAction, WorkflowParticipants } from '../types/workflow';
import { WorkflowTypeType } from '../types/workflow-type';

@Injectable()
export class WorkflowService {
  private readonly repository = inject(WorkflowRepository);
  private readonly injector = inject(Injector);
  private readonly userInfoService = inject<UserInfoService<UserInfo>>(UserInfoService);
  private readonly router = inject(Router);

  readonly viewMode = signal<'kanban' | 'table'>('table');

  public readonly selectedWorkflowType = signal<string | null>(null);

  private readonly workflowsResource = resource({
    params: () => this.selectedWorkflowType(),
    loader: async ({ params }) => {
      try {
        if (params === null) {
          return (await this.repository.loadAll()) ?? [];
        }
        return (await this.repository.queryWorkflows(params)) ?? [];
      } catch (error) {
        handleRequestError(error, this.injector, { show: true });
        return [];
      }
    },
    defaultValue: [],
  });
  public readonly all = this.workflowsResource.value;
  public readonly resource = this.workflowsResource.asReadonly();
  public readonly allLoading = this.workflowsResource.isLoading;

  private readonly workflowResource = resource({
    params: () => this.workflowId(),
    loader: async ({ params }) => {
      if (params === null) {
        return null;
      }
      try {
        const response = await this.repository.loadById(params);
        return response as Workflow;
      } catch (error) {
        handleRequestError(error, this.injector, { show: true });
        return null;
      }
    },
  });
  public readonly workflowId = signal<number | null>(null);
  public readonly value = this.workflowResource.value;
  public readonly valueLoading = this.workflowResource.isLoading;

  private readonly workflowHistoryResource = resource({
    params: () => this.workflowId(),
    loader: async ({ params }) => {
      if (params === null) {
        return [];
      }
      try {
        const response = await this.repository.loadHistory(params);
        return response ?? [];
      } catch (error) {
        handleRequestError(error, this.injector, { show: true });
        return [];
      }
    },
  });
  public readonly history = this.workflowHistoryResource.value;
  public readonly historyLoading = this.workflowHistoryResource.isLoading;

  private readonly workflowCommentsResource = resource({
    params: () => this.workflowId(),
    loader: async ({ params }) => {
      if (params === null) {
        return [];
      }
      try {
        const response = await this.repository.loadComments(params);
        return response ?? [];
      } catch (error) {
        handleRequestError(error, this.injector, { show: true });
        return [];
      }
    },
  });
  public readonly comments = this.workflowCommentsResource.value;
  public readonly commentsLoading = this.workflowCommentsResource.isLoading;

  async addComment(workflowId: number, message: string) {
    const newComment = await this.repository.createComment({ message }, { workflowId });
    const comments = [...(this.workflowCommentsResource.value() ?? []), newComment];
    this.workflowCommentsResource.set(comments);
    return comments;
  }

  async deleteComment(commentId: number | string) {
    await this.repository.deleteComment({
      workflowId: this.workflowId()!,
      commentId,
    });
    const comments = (this.workflowCommentsResource.value() ?? []).filter(
      (c) => c.id !== commentId
    );
    this.workflowCommentsResource.set(comments);
    return comments;
  }

  async createNextAction(
    workflowId: number,
    body: WorkflowAction
  ): Promise<Workflow | undefined> {
    try {
      const updatedWorkflow = await this.repository.createNextAction(body, {
        id: workflowId,
      });
      this.workflowResource.set(updatedWorkflow);
      this.workflowsResource.update((workflows) =>
        (workflows ?? []).map((w) => (w.id === workflowId ? updatedWorkflow : w))
      );
      return updatedWorkflow;
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
      return undefined;
    }
  }

  async participantCheck(workflow: Workflow): Promise<void> {
    const currentUser = this.userInfoService.value()?.userName;
    const participants = workflow.currentAction?.participants?.currentParticipants ?? [];
    const isStillParticipant = participants.some(
      (p) => p.name.toLowerCase() === currentUser?.toLowerCase()
    );
    if (!isStillParticipant) {
      this.refresh();
      await this.router.navigate(['/workflow/overview']);
    }
  }

  async updateParticipants(
    workflowId: number,
    participants: WorkflowParticipants
  ): Promise<void> {
    try {
      const updatedWorkflow = await this.repository.updateParticipants(participants, {
        id: workflowId,
      });
      this.workflowResource.set(updatedWorkflow);
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
    }
  }

  async refresh(): Promise<void> {
    this.workflowsResource.reload();
  }

  async createWorkflow(type: WorkflowTypeType): Promise<Workflow | undefined> {
    try {
      return await this.repository.createWorkflow(type);
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
      return undefined;
    }
  }
}
